import { LoopIcon, ShuffleIcon } from '@radix-ui/react-icons';
import { IconButton, Spinner } from '@radix-ui/themes';
import { CustomIcon } from 'components/common/custom-icon';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTooltip } from 'components/common/tooltip';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { FiringContext } from 'contexts/firing/context';
import { CustomIconPath } from 'enums/custom.enums';
import { IgnoreAutoFireMode } from 'enums/firing';
import { IQueueDefinition, QueueType } from 'interfaces/i-queue-mode';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo } from 'react';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const QUEUE_TYPES = Object.values(QueueType);

export const PLSidebarQueueButton = () => {
  const loadingTms = useTrainedMsStore((s) => s.loading);

  const { safeSetIgnoreAf } = useContext(FiringContext);
  const queueType = usePitchListStore((s) => s.queueType);

  const definition = useMemo(() => {
    switch (queueType) {
      case QueueType.RepeatOne: {
        const o: IQueueDefinition = {
          type: QueueType.RepeatOne,
          label: 'Repeat One',
          tooltip: 'Repeat a pitch until a new one is selected.',
          icon: <CustomIcon icon={CustomIconPath.RepeatOne} />,
        };
        return o;
      }

      case QueueType.RepeatAll: {
        const o: IQueueDefinition = {
          type: QueueType.RepeatAll,
          label: 'Repeat All',
          tooltip:
            'Repeat all pitches in list according to the current sequence.',
          icon: <LoopIcon />,
        };
        return o;
      }

      case QueueType.ShuffleEach: {
        const o: IQueueDefinition = {
          type: QueueType.ShuffleEach,
          label: 'Shuffle',
          tooltip: 'Select a random next pitch after each fire.',
          icon: <ShuffleIcon />,
        };

        return o;
      }

      default: {
        console.warn(`Encountered unexpected queue type ${queueType}`);
        return;
      }
    }
  }, [queueType]);

  if (!definition) {
    return null;
  }

  return (
    <ErrorBoundary identifier="PLSidebarQueueButton">
      <CommonTooltip
        trigger={
          <IconButton
            disabled={loadingTms}
            variant={RADIX.BUTTON.VARIANT.SECONDARY}
            color={RADIX.COLOR.NEUTRAL}
            onClick={() => {
              safeSetIgnoreAf(IgnoreAutoFireMode.Permanent);

              const index = QUEUE_TYPES.findIndex((o) => o === queueType);
              const nextType = QUEUE_TYPES[(index + 1) % QUEUE_TYPES.length];

              usePitchListStore.getState().setQueueType(nextType);
            }}
          >
            {loadingTms ? <Spinner /> : definition.icon}
          </IconButton>
        }
        text={definition.tooltip}
      />
    </ErrorBoundary>
  );
};
